import { useEffect, useRef, useState } from "react";
import { setRiderPayoutAccount, updateRiderSweepSchedule, getBankList, resolveAccountName } from "../api/client";

/**
 * Two standalone cards:
 *  1. PayoutBankAccountCard — bank details (provider, bank, account number, account name).
 *     Account name MUST match the dispatcher's registered name — enforced on the server.
 *  2. AutoSweepCard — sweep schedule only. Can be updated any time, any day without
 *     re-submitting bank details.
 *
 * Props:
 *  - payoutAccount (object|null) — current account, if any
 *  - riderName (string)          — registered name, shown as a hint
 *  - onUpdated(payoutAccount)    — called after a successful bank account save
 *  - onSweepUpdated(schedule)    — called after a successful sweep save
 */
export default function PayoutAccountForm({ payoutAccount, riderName, onUpdated, onSweepUpdated }) {
  return (
    <>
      <PayoutBankAccountCard payoutAccount={payoutAccount} riderName={riderName} onUpdated={onUpdated} />
      <AutoSweepCard payoutAccount={payoutAccount} onSweepUpdated={onSweepUpdated} />
    </>
  );
}

/* ─── 1. Payout Bank Account Card ─────────────────────────────────────────── */
export function PayoutBankAccountCard({ payoutAccount, riderName, onUpdated }) {
  const [form, setForm] = useState({
    provider: "paystack",
    bankCode: "",
    accountNumber: "",
    accountName: "",
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(false);
  const resolveTimer = useRef(null);

  // Load bank list when provider changes
  useEffect(() => {
    setBanksLoading(true);
    setBanks([]);
    setForm((f) => ({ ...f, bankCode: "" }));
    getBankList(form.provider)
      .then(({ data }) => setBanks(data))
      .catch(() => setBanks([]))
      .finally(() => setBanksLoading(false));
  }, [form.provider]);

  // Auto-resolve account name from bank + account number
  useEffect(() => {
    clearTimeout(resolveTimer.current);
    setResolved(false);
    if (!form.bankCode || form.accountNumber.length !== 10) return;

    resolveTimer.current = setTimeout(async () => {
      setResolving(true);
      try {
        const { data } = await resolveAccountName(form.provider, form.bankCode, form.accountNumber);
        if (data.accountName) {
          setForm((f) => ({ ...f, accountName: data.accountName }));
          setResolved(true);
        }
      } catch {
        // silent — user can still type the account name manually
      } finally {
        setResolving(false);
      }
    }, 500);

    return () => clearTimeout(resolveTimer.current);
  }, [form.bankCode, form.accountNumber, form.provider]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const { data } = await setRiderPayoutAccount({
        provider: form.provider,
        bankCode: form.bankCode,
        accountNumber: form.accountNumber,
        accountName: form.accountName,
      });
      setMessage(
        data.payoutAccount.status === "active"
          ? "✓ Payout bank account connected successfully."
          : "Could not verify this account with the provider — please double-check the details."
      );
      onUpdated?.(data.payoutAccount);
    } catch (err) {
      setError(err.response?.data?.error || "Could not save payout account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rider-card">
      <h3>Payout Bank Account</h3>
      {payoutAccount?.status === "active" ? (
        <p className="hint" style={{ color: "var(--success)" }}>
          ✓ Connected — {payoutAccount.provider} · {payoutAccount.accountNumber} ({payoutAccount.accountName})
        </p>
      ) : (
        <p className="hint">Connect a bank account so withdrawals have somewhere to go.</p>
      )}
      {riderName && (
        <p className="hint" style={{ marginBottom: "10px", fontSize: "12px" }}>
          ⚠ At least <strong>two parts</strong> of the account name must match your registered name: <strong>{riderName}</strong>
        </p>
      )}
      <form onSubmit={handleSubmit} style={{ marginTop: "10px" }}>
        <div className="form-field">
          <label>Provider</label>
          <select value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}>
            <option value="paystack">Paystack</option>
            <option value="flutterwave">Flutterwave</option>
          </select>
        </div>
        <div className="form-field">
          <label>Bank</label>
          <select
            value={form.bankCode}
            onChange={e => { setForm(f => ({ ...f, bankCode: e.target.value, accountName: "" })); setResolved(false); }}
            disabled={banksLoading}
            required
          >
            <option value="">{banksLoading ? "Loading banks..." : "Select your bank"}</option>
            {banks.map(b => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Account Number</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={form.accountNumber}
            onChange={e => { setForm(f => ({ ...f, accountNumber: e.target.value.replace(/\D/g, ""), accountName: "" })); setResolved(false); }}
            required
          />
        </div>
        <div className="form-field">
          <label>Account Name{resolving && " — verifying..."}</label>
          <input
            type="text"
            value={form.accountName}
            onChange={e => { setForm(f => ({ ...f, accountName: e.target.value })); setResolved(false); }}
            placeholder={resolving ? "Looking up account name..." : "Auto-fills once bank + account number are entered"}
            required
          />
          {resolved && (
            <p className="hint" style={{ color: "var(--success)", marginTop: "4px" }}>
              ✓ Verified with {form.provider === "flutterwave" ? "Flutterwave" : "Paystack"}
            </p>
          )}
        </div>
        {error && <p className="error">{error}</p>}
        {message && <p className="hint" style={{ color: "var(--success)" }}>{message}</p>}
        <button type="submit" disabled={busy}>
          {busy ? "Saving..." : payoutAccount?.status === "active" ? "Update Payout Account" : "Connect Payout Account"}
        </button>
      </form>
    </div>
  );
}

/* ─── 2. Auto-sweep Card ────────────────────────────────────────────────────── */
const SWEEP_LABELS = {
  manual: "Manual — I'll withdraw myself",
  instant: "Instant — auto-transfer after every completed delivery",
  daily: "Daily — auto-transfer once every 24 hours",
};

export function AutoSweepCard({ payoutAccount, onSweepUpdated }) {
  const [schedule, setSchedule] = useState(payoutAccount?.sweepSchedule || "manual");
  const [preferredAmount, setPreferredAmount] = useState(payoutAccount?.preferredAmount || "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Keep in sync when parent reloads payoutAccount
  useEffect(() => {
    if (payoutAccount?.sweepSchedule) setSchedule(payoutAccount.sweepSchedule);
    if (payoutAccount?.preferredAmount !== undefined) {
      setPreferredAmount(payoutAccount.preferredAmount || "");
    }
  }, [payoutAccount?.sweepSchedule, payoutAccount?.preferredAmount]);

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const amountToSend = schedule === "manual" && preferredAmount ? Number(preferredAmount) : null;
      await updateRiderSweepSchedule(schedule, amountToSend);
      setMessage(
        schedule === "manual" && amountToSend
          ? `✓ Auto-sweep set to Manual (Preferred: ₦${amountToSend.toLocaleString()})`
          : `✓ Auto-sweep set to: ${SWEEP_LABELS[schedule]}`
      );
      onSweepUpdated?.(schedule, amountToSend);
    } catch (err) {
      setError(err.response?.data?.error || "Could not update auto-sweep setting.");
    } finally {
      setBusy(false);
    }
  };

  const isLocked = !payoutAccount || payoutAccount.status !== "active";

  return (
    <div className="rider-card" style={{ marginTop: "16px" }}>
      <h3>Auto-sweep</h3>
      <p className="hint" style={{ marginBottom: "10px" }}>
        {isLocked
          ? "Connect a payout bank account first to enable auto-sweep."
          : `Current: ${SWEEP_LABELS[payoutAccount.sweepSchedule || "manual"]}${payoutAccount.sweepSchedule === "manual" && payoutAccount.preferredAmount ? ` · Preferred: ₦${payoutAccount.preferredAmount.toLocaleString()}` : ""}. You can change this at any time.`}
      </p>
      <form onSubmit={handleSave}>
        <div className="form-field">
          <label>Sweep preference</label>
          <select
            value={schedule}
            onChange={e => setSchedule(e.target.value)}
            disabled={isLocked}
          >
            <option value="manual">Manual — I'll withdraw myself (default)</option>
            <option value="instant">Instant — auto-transfer after every delivery</option>
            <option value="daily">Daily — auto-transfer once a day</option>
          </select>
          <p className="hint" style={{ marginTop: "4px" }}>
            {schedule === "manual"
              ? "Funds stay in your wallet until you tap Withdraw."
              : schedule === "instant"
                ? "Your balance sweeps to your bank account automatically right after every completed delivery."
                : "Your balance sweeps to your bank account automatically once every 24 hours."}
          </p>
        </div>

        {schedule === "manual" && (
          <div className="form-field" style={{ marginTop: "12px" }}>
            <label>
              Preferred Withdrawal Amount (₦) <span className="hint" style={{ fontWeight: "normal" }}>(Optional)</span>
            </label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "14px" }}>₦</span>
              <input
                type="number"
                min="100"
                placeholder="e.g. 5000"
                value={preferredAmount}
                onChange={e => setPreferredAmount(e.target.value)}
                disabled={isLocked}
                style={{ paddingLeft: "28px" }}
              />
            </div>
            <p className="hint" style={{ marginTop: "4px" }}>
              Set your preferred amount for manual withdrawals. You can also withdraw full or custom amounts anytime.
            </p>
          </div>
        )}

        {error && <p className="error">{error}</p>}
        {message && <p className="hint" style={{ color: "var(--success)" }}>{message}</p>}
        <button type="submit" disabled={busy || isLocked}>
          {busy ? "Saving..." : "Save Auto-sweep Setting"}
        </button>
      </form>
    </div>
  );
}
